import { AnimatedContainer, AnimatedSection } from '@components/animated'
import { Labeled } from '@components/form/Labeled'
import { Editor, Section } from '@components/index'
import { SectionTitle } from '@components/section/SectionTitle'
import { useDeleteFlat } from '@services/flats/mutations/useDeleteFlat'
import { FlatWrite } from '@services/flats/types'
import {
  Building2,
  Check,
  Home,
  Landmark,
  Loader2,
  Plus,
  ScrollText,
  Trash2,
  Wallet
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FlatDetail } from 'types/api'

import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MoneyInput } from '@/components/ui/money-input'
import { Switch } from '@/components/ui/switch'

import { FlatLandlordsField } from './FlatLandlordsField'
import { FlatLocationField } from './FlatLocationField'

type FlatsFormProps = {
  data?: FlatDetail
  handleSubmit: (value: FlatWrite) => void
  isLoading?: boolean
}

type TitleDeedPlot = {
  type: string
  plot_of_number: string
  area: number
}

type TitleDeed = {
  type: string
  title_deed: string
  share_of_land: string
  plots: TitleDeedPlot[]
}

type FlatFormValues = {
  title: string
  status: FlatDetail['status']
  location?: string
  landlords: string[]
  cadastre_territory?: string | null
  cadastre_department?: string | null
  address?: string | null
  address_number?: string | null
  address_entrance_number?: string | null
  floor?: string | null
  flat_number?: string | null
  iban?: string | null
  rent?: number | null
  deposit?: number | null
  service_fee_accepted?: boolean
  monthly_advance_prescription?: number | null
  electricity?: number | null
  gas?: number | null
  internet_tv?: number | null
  availability_date?: string | null
  room_count?: number | null
  area?: number | null
  pet_friendly?: boolean
  onboarded?: boolean
  orientation?: string | null
  condition?: FlatDetail['condition']
}

const TODAY_ISO = new Date().toISOString().slice(0, 10)

export default function FlatsForm({
  handleSubmit,
  data,
  isLoading
}: FlatsFormProps) {
  const { t } = useTranslation('flat')
  const { mutate: deleteFlat } = useDeleteFlat()
  const { confirm, dialog } = useConfirm()

  const [description, setDescription] = useState(data?.description || '')
  const [titleDeeds, setTitleDeeds] = useState<TitleDeed[]>([])

  const initialLandlords = Array.isArray(data?.landlords)
    ? (data?.landlords as Array<{ id: string; email?: string }>)
    : []

  const {
    register,
    control,
    handleSubmit: rhfSubmit,
    formState: { errors }
  } = useForm<FlatFormValues>({
    mode: 'onTouched',
    defaultValues: {
      title: data?.title ?? '',
      status: data?.status ?? 'DRAFT',
      location: data?.location?.id,
      landlords: initialLandlords.map((l) => l.id),
      cadastre_territory: data?.cadastre_territory ?? '',
      cadastre_department: data?.cadastre_department ?? '',
      address: data?.address ?? '',
      address_number: data?.address_number ?? '',
      address_entrance_number: data?.address_entrance_number ?? '',
      floor: data?.floor ?? '',
      flat_number: data?.flat_number ?? '',
      iban: data?.iban ?? '',
      rent: data?.rent ?? undefined,
      deposit: data?.deposit ?? undefined,
      service_fee_accepted: data?.service_fee_accepted ?? false,
      monthly_advance_prescription:
        data?.monthly_advance_prescription ?? undefined,
      electricity: data?.electricity ?? undefined,
      gas: data?.gas ?? undefined,
      internet_tv: data?.internet_tv ?? undefined,
      availability_date: data?.availability_date ?? undefined,
      room_count: data?.room_count ?? undefined,
      area: data?.area ?? undefined,
      pet_friendly: data?.pet_friendly ?? false,
      onboarded: data?.onboarded ?? false,
      orientation: data?.orientation ?? '',
      condition: data?.condition ?? 'GOOD'
    }
  })

  // Parse existing title_deeds (stored as a JSON string or array).
  useEffect(() => {
    if (!data?.title_deeds) {
      setTitleDeeds([])
      return
    }
    try {
      const parsed =
        typeof data.title_deeds === 'string'
          ? JSON.parse(data.title_deeds)
          : data.title_deeds
      setTitleDeeds(Array.isArray(parsed) ? parsed : [])
    } catch {
      setTitleDeeds([])
    }
  }, [data])

  useEffect(() => {
    if (data?.description) setDescription(data.description)
  }, [data])

  const statusOptions = [
    { value: 'DRAFT', label: t('statuses.DRAFT') },
    { value: 'RENTED', label: t('statuses.RENTED') },
    { value: 'NOT_AVAILABLE', label: t('statuses.NOT_AVAILABLE') },
    { value: 'AVAILABLE', label: t('statuses.AVAILABLE') }
  ]

  const conditionOptions = [
    { value: 'NEW', label: t('conditions.new') },
    { value: 'RENOVATED', label: t('conditions.renovated') },
    { value: 'AVERAGE', label: t('conditions.average') },
    { value: 'GOOD', label: t('conditions.good') },
    { value: 'BAD', label: t('conditions.bad') }
  ]

  const numberOr = (v: string) => (v === '' ? undefined : Number(v))

  function onValid(values: FlatFormValues) {
    const payload = {
      ...values,
      description,
      title_deeds: titleDeeds.length ? JSON.stringify(titleDeeds) : undefined
    } as Record<string, unknown>

    if (!values.landlords?.length) delete payload.landlords

    handleSubmit(payload as unknown as FlatWrite)
  }

  async function onDelete() {
    if (!data?.id) return
    if (
      await confirm({
        title: t('form.delete_confirm.title'),
        description: t('form.delete_confirm.description'),
        confirmText: t('form.delete_confirm.ok'),
        cancelText: t('form.delete_confirm.cancel'),
        destructive: true
      })
    ) {
      deleteFlat({ id: data.id })
    }
  }

  const invalidCls = (e?: unknown) => (e ? 'border-destructive' : '')

  return (
    <form onSubmit={rhfSubmit(onValid)}>
      <AnimatedContainer>
        <div className="space-y-4">
          {/* --- Basic information --- */}
          <AnimatedSection>
            <Section
              defaultOpen
              title={
                <SectionTitle
                  icon={<Home className="h-4 w-4" />}
                  title={t('form.sections.basic_information')}
                />
              }
            >
              <div className="space-y-4 p-5 pt-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Labeled
                    label={t('form.labels.title')}
                    required
                    error={errors.title?.message}
                    className="sm:col-span-2"
                  >
                    <Input
                      placeholder={t('form.labels.title')}
                      className={invalidCls(errors.title)}
                      {...register('title', {
                        required: t('form.validation.required'),
                        maxLength: {
                          value: 255,
                          message: t('form.validation.maxLength', { count: 255 })
                        }
                      })}
                    />
                  </Labeled>

                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <Labeled label={t('form.labels.status')}>
                        <Combobox
                          searchable={false}
                          value={field.value ?? undefined}
                          onChange={field.onChange}
                          options={statusOptions}
                        />
                      </Labeled>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Controller
                    control={control}
                    name="location"
                    render={({ field }) => (
                      <Labeled label={t('form.labels.location')}>
                        <FlatLocationField
                          value={field.value ?? undefined}
                          onChange={field.onChange}
                        />
                      </Labeled>
                    )}
                  />

                  <Controller
                    control={control}
                    name="landlords"
                    render={({ field }) => (
                      <Labeled label={t('form.labels.landlord')}>
                        <FlatLandlordsField
                          value={field.value ?? []}
                          onChange={field.onChange}
                          initialLandlords={initialLandlords}
                        />
                      </Labeled>
                    )}
                  />

                  <Controller
                    control={control}
                    name="availability_date"
                    render={({ field }) => (
                      <Labeled label={t('form.labels.availability_date')}>
                        <DatePicker
                          clearable
                          min={TODAY_ISO}
                          value={field.value ?? undefined}
                          onChange={field.onChange}
                        />
                      </Labeled>
                    )}
                  />
                </div>

                <div>
                  <Label className="mb-1.5 block">
                    {t('form.labels.options')}
                  </Label>
                  <div className="flex flex-wrap gap-x-10 gap-y-4 rounded-2xl border border-ink/[0.08] bg-ink/[0.015] px-5 py-4">
                    {(
                      [
                        ['pet_friendly', 'pet_friendly'],
                        ['onboarded', 'onboarded']
                      ] as const
                    ).map(([name, labelKey]) => (
                      <Controller
                        key={name}
                        control={control}
                        name={name}
                        render={({ field }) => (
                          <label className="flex cursor-pointer items-center gap-3">
                            <Switch
                              checked={!!field.value}
                              onCheckedChange={field.onChange}
                            />
                            <span className="text-sm font-medium text-ink">
                              {t(`form.labels.${labelKey}`)}
                            </span>
                          </label>
                        )}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-1.5 block">
                    {t('form.labels.description')}
                  </Label>
                  <div className="rounded-2xl border border-ink/[0.16] bg-white p-2">
                    <Editor
                      content={description}
                      onUpdate={(content) => setDescription(content)}
                    />
                  </div>
                </div>
              </div>
            </Section>
          </AnimatedSection>

          {/* --- Flat & address --- */}
          <AnimatedSection>
            <Section
              title={
                <SectionTitle
                  icon={<Building2 className="h-4 w-4" />}
                  title={t('form.sections.flat_and_address')}
                />
              }
            >
              <div className="space-y-6 p-5 pt-2">
                <div>
                  <h3 className="m-0 mb-3 font-display text-base font-bold text-ink">
                    {t('form.labels.address')}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Labeled label={t('form.labels.street')}>
                      <Input {...register('address')} />
                    </Labeled>
                    <Labeled label={t('form.labels.address_number')}>
                      <Input {...register('address_number')} />
                    </Labeled>
                    <Labeled label={t('form.labels.address_entrance_number')}>
                      <Input {...register('address_entrance_number')} />
                    </Labeled>
                  </div>
                </div>

                <div>
                  <h3 className="m-0 mb-3 font-display text-base font-bold text-ink">
                    {t('form.labels.flat_information')}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <Labeled label={t('form.labels.floor')}>
                      <Input {...register('floor')} />
                    </Labeled>
                    <Labeled label={t('form.labels.flat_number')}>
                      <Input {...register('flat_number')} />
                    </Labeled>
                    <Controller
                      control={control}
                      name="room_count"
                      render={({ field }) => (
                        <Labeled label={t('form.labels.room_count')}>
                          <Input
                            type="number"
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(numberOr(e.target.value))
                            }
                            onBlur={field.onBlur}
                          />
                        </Labeled>
                      )}
                    />
                    <Controller
                      control={control}
                      name="area"
                      render={({ field }) => (
                        <Labeled label={t('form.labels.area')}>
                          <Input
                            type="number"
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(numberOr(e.target.value))
                            }
                            onBlur={field.onBlur}
                          />
                        </Labeled>
                      )}
                    />
                    <Labeled label={t('form.labels.orientation')}>
                      <Input {...register('orientation')} />
                    </Labeled>
                    <Controller
                      control={control}
                      name="condition"
                      render={({ field }) => (
                        <Labeled label={t('form.labels.condition')}>
                          <Combobox
                            searchable={false}
                            value={field.value ?? undefined}
                            onChange={field.onChange}
                            options={conditionOptions}
                          />
                        </Labeled>
                      )}
                    />
                  </div>
                </div>
              </div>
            </Section>
          </AnimatedSection>

          {/* --- Cadastre & ownership --- */}
          <AnimatedSection>
            <Section
              title={
                <SectionTitle
                  icon={<Landmark className="h-4 w-4" />}
                  title={t('form.sections.cadastre_ownership')}
                />
              }
            >
              <div className="space-y-6 p-5 pt-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Labeled label={t('form.labels.cadastre_territory')}>
                    <Input {...register('cadastre_territory')} />
                  </Labeled>
                  <Labeled label={t('form.labels.cadastre_department')}>
                    <Input {...register('cadastre_department')} />
                  </Labeled>
                </div>

                <TitleDeedsEditor
                  titleDeeds={titleDeeds}
                  setTitleDeeds={setTitleDeeds}
                />
              </div>
            </Section>
          </AnimatedSection>

          {/* --- Financial information --- */}
          <AnimatedSection>
            <Section
              title={
                <SectionTitle
                  icon={<Wallet className="h-4 w-4" />}
                  title={t('form.sections.financial_information')}
                />
              }
            >
              <div className="space-y-6 p-5 pt-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Labeled label={t('form.labels.iban')}>
                    <Input {...register('iban')} />
                  </Labeled>

                  <Controller
                    control={control}
                    name="rent"
                    render={({ field }) => (
                      <Labeled label={t('form.labels.rent')}>
                        <MoneyInput
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      </Labeled>
                    )}
                  />

                  <Controller
                    control={control}
                    name="deposit"
                    render={({ field }) => (
                      <Labeled label={t('form.labels.deposit')}>
                        <MoneyInput
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      </Labeled>
                    )}
                  />
                </div>

                <Controller
                  control={control}
                  name="service_fee_accepted"
                  render={({ field }) => (
                    <label className="flex w-fit cursor-pointer items-center gap-3">
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                      />
                      <span className="text-sm font-medium text-ink">
                        {t('form.labels.service_fee')}
                      </span>
                    </label>
                  )}
                />

                <div>
                  <h3 className="m-0 mb-3 font-display text-base font-bold text-ink">
                    {t('form.labels.expenses')}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {(
                      [
                        ['monthly_advance_prescription', 'monthly_advance_prescription'],
                        ['electricity', 'electricity'],
                        ['gas', 'gas'],
                        ['internet_tv', 'internet_tv']
                      ] as const
                    ).map(([name, labelKey]) => (
                      <Controller
                        key={name}
                        control={control}
                        name={name}
                        render={({ field }) => (
                          <Labeled label={t(`form.labels.${labelKey}`)}>
                            <MoneyInput
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            />
                          </Labeled>
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </Section>
          </AnimatedSection>

          {/* --- Actions --- */}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {data && (
              <Button
                type="button"
                variant="ghostDark"
                size="lg"
                className="mr-auto"
                onClick={onDelete}
              >
                <Trash2 className="h-4 w-4" />
                {t('form.buttons.delete')}
              </Button>
            )}

            <Button type="submit" size="lg" disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : data ? (
                <Check className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {data ? t('form.labels.update') : t('form.labels.create')}{' '}
              {t('form.labels.flat')}
            </Button>
          </div>
        </div>
      </AnimatedContainer>
      {dialog}
    </form>
  )
}

// --- Title deeds sub-form -------------------------------------------------

function TitleDeedsEditor({
  titleDeeds,
  setTitleDeeds
}: {
  titleDeeds: TitleDeed[]
  setTitleDeeds: React.Dispatch<React.SetStateAction<TitleDeed[]>>
}) {
  const { t } = useTranslation('flat')

  const addDeed = () =>
    setTitleDeeds((prev) => [
      ...prev,
      { type: 'a', title_deed: '', share_of_land: '', plots: [] }
    ])

  const updateDeed = (index: number, patch: Partial<TitleDeed>) =>
    setTitleDeeds((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d))
    )

  const updatePlot = (
    deedIndex: number,
    plotIndex: number,
    patch: Partial<TitleDeedPlot>
  ) =>
    setTitleDeeds((prev) =>
      prev.map((d, i) =>
        i === deedIndex
          ? {
              ...d,
              plots: d.plots.map((p, j) =>
                j === plotIndex ? { ...p, ...patch } : p
              )
            }
          : d
      )
    )

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="m-0 font-display text-base font-bold text-ink">
          {t('form.title_deeds.heading')}
        </h3>
        {titleDeeds.length > 0 && (
          <Button type="button" variant="ghostDark" size="sm" onClick={addDeed}>
            <Plus className="h-4 w-4" />
            {t('form.title_deeds.add')}
          </Button>
        )}
      </div>

      {titleDeeds.length === 0 ? (
        <button
          type="button"
          onClick={addDeed}
          className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-ink/20 bg-ink/[0.015] px-6 py-10 text-center transition-colors hover:border-primary/50 hover:bg-primary/[0.03]"
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ScrollText className="h-5 w-5" />
          </span>
          <span className="text-sm font-medium text-ink">
            {t('form.title_deeds.empty')}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
            <Plus className="h-4 w-4" />
            {t('form.title_deeds.add')}
          </span>
        </button>
      ) : (
        <div className="space-y-4">
          {titleDeeds.map((deed, index) => (
            <div
              key={index}
              className="rounded-2xl border border-ink/[0.1] bg-ink/[0.015] p-4"
            >
              <div className="mb-4 flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
                  <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-bold text-primary">
                    {index + 1}
                  </span>
                  {t('form.title_deeds.card_title')}
                </span>
                <Button
                  type="button"
                  variant="ghostDark"
                  size="sm"
                  onClick={() =>
                    setTitleDeeds((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Labeled label={t('form.labels.title_deed')}>
                  <Input
                    value={deed.title_deed}
                    onChange={(e) =>
                      updateDeed(index, { title_deed: e.target.value })
                    }
                  />
                </Labeled>
                <Labeled label={t('form.labels.share_of_land')}>
                  <Input
                    value={deed.share_of_land}
                    onChange={(e) =>
                      updateDeed(index, { share_of_land: e.target.value })
                    }
                  />
                </Labeled>
                <Labeled label={t('form.labels.contract_plot_type')}>
                  <Combobox
                    searchable={false}
                    value={deed.type}
                    onChange={(v) => updateDeed(index, { type: v ?? 'a' })}
                    options={[
                      { value: 'a', label: 'a' },
                      { value: 'b', label: 'b' }
                    ]}
                  />
                </Labeled>
              </div>

              <div className="mt-5">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('form.title_deeds.plots')}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      updateDeed(index, {
                        plots: [
                          ...(deed.plots ?? []),
                          { type: 'C', plot_of_number: '', area: 0 }
                        ]
                      })
                    }
                  >
                    <Plus className="h-4 w-4" />
                    {t('form.title_deeds.add_plot')}
                  </Button>
                </div>

                {deed.plots?.length > 0 ? (
                  <div className="space-y-3">
                    {deed.plots.map((plot, plotIndex) => (
                      <div
                        key={plotIndex}
                        className="grid grid-cols-1 gap-3 rounded-xl border border-ink/[0.08] bg-white p-3 sm:grid-cols-[7rem_1fr_1fr_auto] sm:items-end"
                      >
                        <Labeled label={t('form.labels.plot_type')}>
                          <Combobox
                            searchable={false}
                            value={plot.type}
                            onChange={(v) =>
                              updatePlot(index, plotIndex, { type: v ?? 'C' })
                            }
                            options={[
                              { value: 'C', label: 'C' },
                              { value: 'E', label: 'E' }
                            ]}
                          />
                        </Labeled>
                        <Labeled label={t('form.labels.plot_of_number')}>
                          <Input
                            value={plot.plot_of_number}
                            onChange={(e) =>
                              updatePlot(index, plotIndex, {
                                plot_of_number: e.target.value
                              })
                            }
                          />
                        </Labeled>
                        <Labeled label={t('form.labels.plot_area')}>
                          <Input
                            type="number"
                            value={plot.area?.toString() ?? ''}
                            onChange={(e) =>
                              updatePlot(index, plotIndex, {
                                area: parseFloat(e.target.value) || 0
                              })
                            }
                          />
                        </Labeled>
                        <Button
                          type="button"
                          variant="ghostDark"
                          size="icon"
                          onClick={() =>
                            updateDeed(index, {
                              plots: deed.plots.filter((_, j) => j !== plotIndex)
                            })
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-xl border border-dashed border-ink/15 bg-white px-3 py-3 text-sm text-muted-foreground">
                    {t('form.title_deeds.plots_empty')}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
